import { motion } from "framer-motion";
import { useRef, useCallback, useState } from "react";
import { useModals } from "../../contexts/ModalsContext";
import {
  option,
  OptionGroup,
  Product,
  ProductCardType,
} from "../../types/ProductType";
import ProductOptionGroup from "../Product/ProductOptionGroup";
import ProductAddon from "../Product/ProductAddon";
import ProductDescription from "../Product/ProductDescription";
import { FormikErrors, useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Branch } from "../../types/globalTypes";
import ProductBranchesInput from "../Product/ProductBranchesInput";
import { formatDateForInput, formatMoneyForInput } from "@/utils/helpers";
import { IoMdClose } from "react-icons/io";
import ProductCostInput from "../Product/ProductCostInput";
import { useTranslation } from "react-i18next";
import { useUserRole } from "@/hooks/useUserRole";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";

const modalVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 0.5 },
  exit: { opacity: 0 },
};

type ProductDataModalProps = Pick<
  ProductCardType,
  "_id" | "nameAR" | "nameEN"
> & {
  children?: React.ReactNode;
  FormId: string;
};

type FormikError = string | FormikError[] | { [key: string]: FormikError };
const flattenErrors = (input: FormikError[]): string[] => {
    const result: string[] = [];
    
    const extractStrings = (item: FormikError) => {
        if (typeof item === 'string') {
            result.push(item);
        } else if (Array.isArray(item)) {
            item.forEach(extractStrings);
        } else if (typeof item === 'object' && item !== null) {
            Object.values(item).forEach(extractStrings);
        }
    };
    
    input.forEach(extractStrings);
    return result;
};

export type ProductDataModalBodyProps = Pick<
  Product,
  | "addons"
  | "options"
  | "lowPrice"
  | "lowPriceEndDate"
  | "calories"
  | "salt"
  | "caffeine"
  | "descriptionAR"
  | "descriptionEN"
  | "branches"
  | "productCost"
> & {
  FormId: string;
  onSubmitProductDataModal: (
    values: Pick<
      Product,
      | "addons"
      | "options"
      | "lowPrice"
      | "lowPriceEndDate"
      | "calories"
      | "salt"
      | "caffeine"
      | "descriptionAR"
      | "descriptionEN"
      | "productCost"
    >,
  ) => void;
  allBranches: Branch[];
};

function ProductDataModalBody({
  calories,
  salt,
  caffeine,
  descriptionAR,
  descriptionEN,
  addons,
  lowPrice,
  lowPriceEndDate,
  options,
  allBranches,
  FormId,
  branches,
  onSubmitProductDataModal,
  productCost,
}: ProductDataModalBodyProps) {
  const { setOpendProductDataModalId } = useModals();
  const [addonIds, setAddonIds] = useState<number[]>(
    addons?.map((_, i) => Date.now() + i) || [],
  );
  const { t } = useTranslation();
  const { isAdmin } = useUserRole();
  const [isLowPriceFoucused, setIsLowPriceFocused] = useState(false);
  const [isNutritionOpen, setIsNutritionOpen] = useState(
    !!caffeine || !!salt || !!calories,
  );

  const validationSchema = Yup.object({
    branches: isAdmin
      ? Yup.array().min(1, t("productModal.branchRequired") as string)
      : Yup.array(),
    productCost: Yup.number().positive(
      t("productModal.costPositive") as string,
    ),
    lowPrice: Yup.number()
      .nullable()
      .test(
        "lowPrice-required-if-endDate",
        t("productModal.loweredPriceRequiredIfEndDate") as string,
        function (value) {
          const { lowPriceEndDate } = this.parent;
          return lowPriceEndDate
            ? value !== null && value !== undefined && value > 0
            : true;
        },
      )
      .positive(t("productModal.loweredPricePositive") as string),
    lowPriceEndDate: Yup.date()
      .nullable()
      .test(
        "is-today-or-future",
        t("productModal.dateTodayOrLater") as string,
        (value) =>
          !value ||
          new Date(value).setHours(0, 0, 0, 0) >=
            new Date().setHours(0, 0, 0, 0), // Ensure it's today or later
      )
      .test(
        "endDate-required-if-lowPrice",
        t("productModal.endDateRequiredIfLoweredPrice") as string,
        function (value) {
          const { lowPrice } = this.parent;
          return lowPrice ? value !== undefined : true; // If lowPrice exists, end date is required
        },
      )
      .max(new Date("2200-12-31"), t("productModal.dateBefore2200") as string),
    descriptionAR: Yup.string(),
    descriptionEN: Yup.string().test(
      "at-least-one-lang-description",
      t("productModal.nameRequired") as string,
      function (value) {
        const { descriptionAR } = this.parent;
        return Boolean(value?.trim() || descriptionAR?.trim());
      },
    ),

    calories: Yup.number().positive(t("addProductButton.mustBePositive") as string),
    salt: Yup.number().positive(t("addProductButton.mustBePositive") as string),
    caffeine: Yup.number().positive(t("addProductButton.mustBePositive") as string),

    addons: Yup.array().of(
      Yup.object().shape({
        nameAR: Yup.string(),
        nameEN: Yup.string().test(
          "at-least-one-lang-AddonName",
          t("productModal.nameRequired") as string,
          function (value) {
            const { nameAR } = this.parent;
            return Boolean(value?.trim() || nameAR?.trim());
          },
        ),
        price: Yup.number().min(0, t("productModal.priceNegative") as string),
      }),
    ),

    options: Yup.array().of(
      Yup.object().shape({
        nameAR: Yup.string(),
        nameEN: Yup.string().test(
          "at-least-one-lang-GroupName",
          t("productModal.nameRequired") as string,
          function (value) {
            const { nameAR } = this.parent;
            return Boolean(value?.trim() || nameAR?.trim());
          },
        ),
        limit: Yup.number()
          .required(t("productModal.groupLimitRequired") as string)
          .positive(t("productModal.groupLimitPositive") as string),
        options: Yup.array()
          .of(
            Yup.object().shape({
              nameAR: Yup.string(),
              nameEN: Yup.string().test(
                "at-least-one-lang-OptionName",
                t("productModal.nameRequired") as string,
                function (value) {
                  const { nameAR } = this.parent;
                  return Boolean(value?.trim() || nameAR?.trim());
                },
              ),
              price: Yup.number().min(
                0,
                t("productModal.priceNegative") as string,
              ),
            }),
          )
          .min(1, t("productModal.optionFillOne") as string),
      }),
    ),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      productCost,
      calories,
      salt,
      caffeine,
      lowPrice,
      lowPriceEndDate: formatDateForInput(lowPriceEndDate),
      descriptionAR,
      descriptionEN,
      addons: addons || [],
      options: options || [],
      branches,
    },
    validationSchema,
    onSubmit: (values) => {
      const {
        addons,
        options,
        calories,
        salt,
        caffeine,
        descriptionAR,
        descriptionEN,
        lowPrice,
        lowPriceEndDate,
        branches,
        productCost,
      } = values;

      const IsUserSelectAllBranches =
        values.branches?.length === 0 && allBranches?.length === 0
          ? false
          : values.branches?.length === allBranches?.length;

      const FormatedLowPriceEndDate = lowPriceEndDate
        ? new Date(lowPriceEndDate).getTime() / 1000
        : undefined;

      const newValues = {
        addons,
        options,
        productCost: productCost,
        calories: calories ? calories : undefined,
        salt: salt ? salt : undefined,
        caffeine: caffeine ? caffeine : undefined,
        lowPrice: lowPrice ? lowPrice : undefined,
        descriptionAR,
        descriptionEN,
        allBranchesAvailable: isAdmin ? IsUserSelectAllBranches : undefined,
        branches: isAdmin
          ? branches.length > 0
            ? branches
            : undefined
          : undefined,
        lowPriceEndDate: FormatedLowPriceEndDate,
      };

      // Pass The New Validated Fromated Value to the card to handle it in the update.
      onSubmitProductDataModal(newValues);
      toast.success(t("productModal.unsavedChanges") as string, {
        duration: 4000,
      });
      setOpendProductDataModalId(null);
    },
  });

  // Add Addon Handler
  const handleAddAddon = () => {
    const newAddon = { nameAR: "", nameEN: "", price: 0 };
    const newId = Date.now(); // Generate a unique ID
    setAddonIds((prevIds) => [newId,...prevIds ]); // Store the ID
    formik.setFieldValue("addons", [newAddon,...formik.values.addons]);
  };

  // Add Group Handler
  const handleAddGroup = () => {
    const newGroup = { nameAR: "", nameEN: "", limit: 1, options: [] };
    formik.setFieldValue("options", [newGroup,...formik.values.options]);
  };

  const handleDeleteAddon = (index: number) => {
    setAddonIds((prevIds) => prevIds.filter((_, i) => i !== index)); // Remove the ID
    formik.setFieldValue(
      "addons",
      formik.values.addons.filter((_, i) => i !== index),
    );
  };

  const handleDeleteGroup = (index: number) => {
    formik.setFieldValue(
      "options",
      formik.values.options.filter((_, i) => i !== index),
    );
  };

  const handleDeleteSingleOption = (groupIndex: number, index: number) => {
    formik.setFieldValue(
      "options",
      formik.values.options.map((group, i) =>
        i === groupIndex
          ? { ...group, options: group.options.filter((_, i) => i !== index) }
          : group,
      ),
    );
  };

  const isCaffineOrSaltOrCaloriesError =
    (formik.errors.caffeine && formik.touched.caffeine) ||
    (formik.errors.salt && formik.touched.salt) ||
    (formik.errors.calories && formik.touched.calories);

  
  const showErrorsToast = () => {
      
    if(Object.keys(formik.errors).length > 0){
        const StringArrayErrors = flattenErrors(Object.values(formik.errors))
        const firstErrorMessage = StringArrayErrors[0]
      
          if (firstErrorMessage && (typeof firstErrorMessage === 'string')) {
            toast.error(firstErrorMessage as string);
          }
          }
    }

  return (
    <form 
    onSubmit={(e) => {
      e.preventDefault()
      showErrorsToast()
      formik.handleSubmit()}
    } 
      id={`${FormId}-product-data-form`}>
      <div className="flex size-full flex-col gap-4 rounded-t-md bg-white p-2 text-black">
        {/* Product Description */}
        <div className="space-y-1">
          <label
            className="text-sm font-semibold capitalize text-gray-700"
            htmlFor="productDescription"
          >
            {t("productModal.productDescription")}
          </label>
          <ProductDescription
            productDescriptions={{
              ar: formik.values.descriptionAR,
              en: formik.values.descriptionEN,
            }}
            onDescriptionChange={(lang, value) =>
              formik.setFieldValue(`description${lang.toUpperCase()}`, value)
            }
          />
          {formik.errors.descriptionAR && formik.touched.descriptionAR && (
            <div className="text-sm text-red-600">
              {formik.errors.descriptionAR}
            </div>
          )}
          {formik.errors.descriptionEN && formik.touched.descriptionEN && (
            <div className="text-sm text-red-600">
              {formik.errors.descriptionEN}
            </div>
          )}
        </div>

        {/* Product Cost */}
        <div className="flex flex-col gap-1">
          <label>{t("productModal.productCost")}</label>
          <ProductCostInput
            place="modal"
            value={formik.values.productCost as number}
            onChange={(value) => formik.setFieldValue("productCost", value)}
          />
          {formik.touched.productCost && formik.errors.productCost && (
            <div className="text-xs text-red-500">
              {formik.errors.productCost}
            </div>
          )}
        </div>

        <div className="grid size-full grid-cols-2 gap-3">
          {/* Lowered Price */}
          <div className="relative col-start-1 col-end-2 size-full space-y-1">
            <label htmlFor="lowPrice">{t("productModal.loweredPrice")}</label>
            <input
              type="number"
              name="lowPrice"
              placeholder={t("productModal.loweredPrice") as string}
              // value={formik.values.lowPrice}
              value={
                isLowPriceFoucused
                  ? formik.values.lowPrice
                  : formatMoneyForInput(formik.values.lowPrice)
              }
              onFocus={() => setIsLowPriceFocused(true)}
              onBlur={() => setIsLowPriceFocused(false)}
              onChange={formik.handleChange}
              className="size-full h-12 rounded-sm border px-2"
              step={0.1}
            />
            {formik.errors.lowPrice && formik.touched.lowPrice && (
              <div className="text-sm text-red-600">
                {formik.errors.lowPrice}
              </div>
            )}
          </div>

          {/* Lowered Price End Date */}
          <div className="col-start-2 col-end-3 size-full space-y-1">
            <label htmlFor="lowPriceEndDate">
              {t("productModal.loweredPriceEndDate")}
            </label>
            <div className="relative">
              <input
                type="date"
                name="lowPriceEndDate"
                value={formik.values.lowPriceEndDate}
                onChange={(e) => {
                  const value = e.target.value;
                  formik.setFieldValue("lowPriceEndDate", value);
                }}
                className="size-full h-12 rounded-sm border px-2"
              />
              {formik.values.lowPriceEndDate && (
                <button
                  type="button"
                  onClick={() => formik.setFieldValue("lowPriceEndDate", "")}
                  className="absolute right-[2.5rem] top-1/2 -translate-y-1/2 transform text-2xl text-red-400 hover:text-red-700"
                >
                  &times;
                </button>
              )}
            </div>

            {formik.errors.lowPriceEndDate &&
              formik.touched.lowPriceEndDate && (
                <div className="text-sm text-red-600">
                  {formik.errors.lowPriceEndDate}
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Branches */}
      {isAdmin && (
        <div className="flex flex-col gap-1 p-2">
          <label className="text-black">
            {t("productModal.branchAvailability")}
          </label>
          <ProductBranchesInput
            allUserBranches={allBranches}
            userPrevSelectedBranches={branches}
            placeholder={t("productModal.branches") as string}
            onBranchChange={(selectedBranches) =>
              formik.setFieldValue("branches", selectedBranches)
            }
            error={
              formik.touched.branches && formik.errors.branches
                ? Array.isArray(formik.errors.branches)
                  ? formik.errors.branches.join(", ")
                  : formik.errors.branches
                : undefined
            }
          />
        </div>
      )}

      {/* Product Options */}
      <div className="mt-5 flex size-full flex-col gap-5 rounded-md border p-5">
        <h1 className="mb-1 text-xl font-semibold text-black">
          {t("productModal.productOptions")}
        </h1>
        <button
          type="button"
          className="mb-2 me-2 size-fit rounded-md bg-sidebar px-5 py-2 text-sm font-medium text-skyblue"
          onClick={handleAddGroup}
        >
          {t("productModal.addOptionGroup")}
        </button>
        {formik.values.options.map((group, index) => (
          <ProductOptionGroup
            key={index}
            onDeleteGroup={handleDeleteGroup}
            onDeleteOption={handleDeleteSingleOption}
            groupIndex={index}
            {...group}
            onChange={(newGroup) =>
              formik.setFieldValue("options", [
                ...formik.values.options.slice(0, index),
                newGroup,
                ...formik.values.options.slice(index + 1),
              ])
            }
            errorMustFillOneOption={
              // Check if the options array is empty or if an option is added
              formik.values.options[index]?.options?.length === 0
                ? (t("productModal.optionFillOne") as string)
                : ""
            }
            errorNameAR={
              (formik.errors.options?.[index] as FormikErrors<OptionGroup>)
                ?.nameAR || ""
            }
            errorNameEN={
              (formik.errors.options?.[index] as FormikErrors<OptionGroup>)
                ?.nameEN || ""
            }
            errorLimit={
              (formik.errors.options?.[index] as FormikErrors<OptionGroup>)
                ?.limit || ""
            }
            errorOptions={
              ((formik.errors.options?.[index] as FormikErrors<OptionGroup>)
                ?.options || []) as FormikErrors<option>[]
            }
          />
        ))}
      </div>

      {/* Product Addons */}
      <div className="mt-5 size-full rounded-md border p-5">
        <h1 className="mb-5 text-xl font-semibold text-black">
          {t("productModal.productAddons")}
        </h1>
        <button
          type="button"
          className="mb-6 me-2 rounded-md bg-sidebar px-5 py-2 text-sm font-medium text-skyblue"
          onClick={handleAddAddon}
        >
          {t("productModal.addAddon")}
        </button>

        <div className="flex flex-col gap-3">
          {formik.values.addons?.length > 0 ? (
            <h3 className="text-lg font-semibold">
              {t("productModal.addons")}
            </h3>
          ) : null}
          {formik.values.addons.map((addon, index) => (
            <ProductAddon
              key={addonIds[index]} // Use the ID as the key
              {...addon}
              onChange={(newAddon) =>
                formik.setFieldValue("addons", [
                  ...formik.values.addons.slice(0, index),
                  newAddon,
                  ...formik.values.addons.slice(index + 1),
                ])
              }
              onDelete={handleDeleteAddon}
              index={index}
              error={
                formik.errors.addons?.[index] &&
                typeof formik.errors.addons[index] !== "string"
                  ? formik.errors.addons[index].nameAR ||
                    formik.errors.addons[index].nameEN ||
                    formik.errors.addons[index].price
                  : undefined
              }
            />
          ))}
        </div>

        <div
          className={`mt-5 rounded-md border ${isCaffineOrSaltOrCaloriesError && !isNutritionOpen ? "border-red-500 bg-red-50" : ""}`}
        >
          {/* Accordion Header */}
          <button
            type="button"
            className="flex w-full items-center justify-between p-5 text-left"
            onClick={() => setIsNutritionOpen(!isNutritionOpen)}
          >
            <h1 className="text-xl font-semibold text-black">
              {t("addProductButton.nutritions-info")}
            </h1>
            <svg
              className={`h-5 w-5 transform transition-transform duration-200 ${
                isNutritionOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Accordion Content (Nutrition) */}
          <div
            className={`overflow-hidden transition-all duration-300 ${
              isNutritionOpen ? "max-h-[500px] p-5" : "max-h-0 p-0"
            }`}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {/* Calories */}
              <div className="space-y-1">
                <label htmlFor="calories">
                  {t("addProductButton.calories")}
                </label>
                <input
                  type="number"
                  name="calories"
                  placeholder={t("addProductButton.calories") as string}
                  value={formik.values.calories}
                  onChange={formik.handleChange}
                  className="h-12 w-full rounded-sm border px-2"
                />
                {formik.errors.calories && formik.touched.calories && (
                  <div className="text-sm text-red-600">
                    {formik.errors.calories}
                  </div>
                )}
              </div>

              {/* Salt */}
              <div className="space-y-1">
                <label htmlFor="salt">{t("addProductButton.salt")}</label>
                <input
                  type="number"
                  name="salt"
                  placeholder={t("addProductButton.salt_label") as string}
                  value={formik.values.salt}
                  onChange={formik.handleChange}
                  className="h-12 w-full rounded-sm border px-2"
                />
                {formik.errors.salt && formik.touched.salt && (
                  <div className="text-sm text-red-600">
                    {formik.errors.salt}
                  </div>
                )}
              </div>

              {/* Caffeine */}
              <div className="space-y-1">
                <label htmlFor="caffeine">
                  {t("addProductButton.caffeine")}
                </label>
                <input
                  type="number"
                  name="caffeine"
                  placeholder={t("addProductButton.caffeine_label") as string}
                  value={formik.values.caffeine}
                  onChange={formik.handleChange}
                  className="h-12 w-full rounded-sm border px-2"
                />
                {formik.errors.caffeine && formik.touched.caffeine && (
                  <div className="text-sm text-red-600">
                    {formik.errors.caffeine}
                  </div>
                )}
              </div>

            </div>

          </div>
        </div>



      </div>
    </form>
  );
}

export default ProductDataModalBody;

export function ProductDataModal({
  _id,
  children,
  nameAR,
  nameEN,
  FormId,
}: ProductDataModalProps) {
  const { t } = useTranslation();

  const { setOpendProductDataModalId, OpendProductDataModalId } = useModals();
  const modalRef = useRef<HTMLDivElement>(null);
  useLockBodyScroll(!!OpendProductDataModalId);
  const closeModal = useCallback(() => {
    setOpendProductDataModalId(null);
  }, [setOpendProductDataModalId]);

  // useEffect(() => {
  //   const handleClickOutside = (event: MouseEvent) => {
  //     if (
  //       modalRef.current &&
  //       !modalRef.current.contains(event.target as Node)
  //     ) {
  //       closeModal();
  //     }
  //   };

  //   document.addEventListener("mousedown", handleClickOutside);

  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, [closeModal]);

  if (OpendProductDataModalId !== _id) return null;

  return (
    <>
      <motion.div
        className="fixed inset-0 z-[9999] bg-black bg-opacity-50"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={overlayVariants}
        // onClick={closeModal}
      />
      <motion.div
        ref={modalRef}
        className="fixed inset-0 z-[9999] mt-14 flex items-start justify-center"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={modalVariants}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <div className="w-[92%] rounded-lg bg-white shadow-lg sm:w-[80%] lg:w-[60%] xl:w-[50%]">
          <div className="flex items-center justify-between border-b p-3 px-5">
            <h2 className="text-lg font-semibold">
              {t("productModal.productData")} -{" "}
              {nameAR && nameEN
                ? t(nameEN, nameAR)
                : nameAR && !nameEN
                  ? nameAR
                  : nameEN && !nameAR
                    ? nameEN
                    : ""}
            </h2>
            <button
              className="p-1 text-2xl font-bold text-red-500"
              onClick={closeModal}
            >
              <IoMdClose size={24} className="text-red-600" />
            </button>
          </div>
          {/* body */}
          <div className="thin-scrollbar max-h-[65dvh] space-y-2 overflow-y-auto p-4 px-5 text-gray-700">
            {children}
          </div>
          {/* footer */}
          <div className="flex items-center justify-end gap-2 rounded-b-lg border-t bg-white px-3 py-3">
            <button
              type="submit"
              form={`${FormId}-product-data-form`}
              className="me-2 rounded-md bg-sidebar px-5 py-2 text-sm font-medium text-skyblue"
            >
              {t("productModal.submit")}
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
}
